import { useTranslation } from "@sagot/i18n";
import { useSearchParams } from "react-router";
import { useFormatters } from "../shared/format";
import { PageHeader } from "../shared/page-header";
import { isPlanGated, UpgradeCard } from "../shared/plan-gate";
import { AnalyticsFiltersBar } from "./analytics-filters";
import { AutomationSection } from "./automation-card";
import { calendarDate, readFilters, writeFilters, type AnalyticsFilters } from "./analytics-model";
import { CsatCard } from "./csat-card";
import { BusiestHoursCard, ChannelSplitCard } from "./distribution-cards";
import { FeedbackCard, LanguageCard } from "./insight-cards";
import { KpiGrid } from "./kpi-grid";
import { QualityCard } from "./quality-card";
import { TopicsCard } from "./topics-card";
import { ConversationsTrendCard, WeeklyOutcomesCard } from "./trend-cards";
import { useSummary } from "./use-analytics";

/** `/w/:ws/analytics`: KPIs, trends, channels, busiest hours, topics, language, feedback, quality. */
export function AnalyticsPage() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const summary = useSummary(filters);
  const onChange = (next: AnalyticsFilters) => {
    setParams(writeFilters(next), { replace: true });
  };
  const period = summary.data?.period;
  const day = (iso: string) => fmt.date(calendarDate(iso));

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <PageHeader
          title={t("engage.analytics.title")}
          subtitle={
            <>
              {t("engage.analytics.subtitle")}{" "}
              {period ? <span className="text-fg-subtle">{t("engage.analytics.period", { start: day(period.start), end: day(period.end) })}</span> : null}
            </>
          }
        />
        <AnalyticsFiltersBar filters={filters} onChange={onChange} />
      </div>
      {isPlanGated(summary.error) ? (
        <UpgradeCard feature={t("engage.analytics.title")} />
      ) : (
        <>
          <KpiGrid query={summary} />
          <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ConversationsTrendCard filters={filters} />
            <WeeklyOutcomesCard filters={filters} />
          </section>
          <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChannelSplitCard filters={filters} />
            <BusiestHoursCard filters={filters} />
          </section>
          <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <TopicsCard filters={filters} />
            <LanguageCard filters={filters} />
          </section>
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            <FeedbackCard filters={filters} />
            <CsatCard filters={filters} />
            <QualityCard filters={filters} />
          </section>
          <AutomationSection filters={filters} />
        </>
      )}
    </div>
  );
}
